import { useNavigate } from 'react-router-dom';
import { Button } from '@almacenes/ui';
import logo from '@/assets/logos/logo.svg';
import { useAuthStore } from '@almacenes/core';

/**
 * Página de éxito después de registrar una tarjeta de crédito (sin cargo automático).
 * Se muestra cuando el usuario completa el proceso de registro de tarjeta con processPayment=false.
 * 
 * Flujo:
 * 1. Usuario hace clic en "PAGAR" (plan manual) en SuscripcionPage
 * 2. Se llama a registerCreditCard con processPayment=false
 * 3. Usuario es redirigido a Flow para ingresar datos de tarjeta
 * 4. Flow redirige de vuelta a esta página con token de confirmación
 * 5. Usuario ve mensaje de éxito y puede volver a gestión de suscripciones
 */
export const RegistroTarjetaExitoPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleVolverSuscripcion = () => {
    navigate('/suscripcion');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F3F4F9' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
        <img 
          src={logo} 
          alt="Almacenes Digitales" 
          className="h-9"
        />
        <Button
          variant="danger"
          onClick={handleLogout}
          size="sm"
        >
          Cerrar sesión
        </Button>
      </div>

      {/* Contenido */}
      <div className="flex-1 flex items-center justify-center px-8 py-4">
        <div className="max-w-3xl w-full">
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            {/* Icono de éxito - Check verde */}
            <div className="flex justify-center mb-6">
              <svg 
                width="120" 
                height="120" 
                viewBox="0 0 120 120" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="60" cy="60" r="60" fill="#10B981"/>
                <path 
                  d="M34 62L50 78L86 42" 
                  stroke="white" 
                  strokeWidth="8" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            
            {/* Título */}
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#11194F' }}>
              ¡Tarjeta Registrada!
            </h1>
            
            {/* Descripción */}
            <p className="text-gray-600 text-lg mb-10 leading-relaxed">
              Tu tarjeta de crédito ha sido registrada exitosamente en nuestro sistema.
            </p>

            {/* Beneficios */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {/* Beneficio 1 */}
              <div className="bg-green-50 rounded-xl p-6 text-left">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">⚡</span>
                  <div>
                    <h3 className="text-green-800 font-semibold mb-2">
                      Pagos Automáticos
                    </h3>
                    <p className="text-green-700 text-sm">
                      Tu suscripción se renovará automáticamente. Olvídate de recordar fechas de pago.
                    </p>
                  </div>
                </div>
              </div>

              {/* Beneficio 2 */}
              <div className="bg-green-50 rounded-xl p-6 text-left">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">🔒</span>
                  <div>
                    <h3 className="text-green-800 font-semibold mb-2">
                      Datos Protegidos
                    </h3>
                    <p className="text-green-700 text-sm">
                      Tu información está segura con Flow, procesador líder en pagos en Chile.
                    </p>
                  </div>
                </div>
              </div>

              {/* Beneficio 3 */}
              <div className="bg-green-50 rounded-xl p-6 text-left">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">💳</span>
                  <div>
                    <h3 className="text-green-800 font-semibold mb-2">
                      Gestión Fácil
                    </h3>
                    <p className="text-green-700 text-sm">
                      Actualiza o elimina tu tarjeta cuando quieras desde tu panel de suscripciones.
                    </p>
                  </div>
                </div>
              </div>

              {/* Beneficio 4 */}
              <div className="bg-green-50 rounded-xl p-6 text-left">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">📊</span>
                  <div>
                    <h3 className="text-green-800 font-semibold mb-2">
                      Sin Interrupciones
                    </h3>
                    <p className="text-green-700 text-sm">
                      Acceso continuo a todas las funcionalidades de Almacenes Digitales.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Botón para volver */}
            <Button
              variant="primary"
              onClick={handleVolverSuscripcion}
              className="w-full py-4 text-lg font-semibold"
              size="lg"
            >
              Volver a Gestión de Suscripciones
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
